<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { ImageFieldProps } from '../types';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { getDefaultContextMenuActions } from '@/components/ContextMenu/utils';
import { mdiUpload } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import { useFileDialog } from '@vueuse/core';
import { defaultFileUploadOptions, UploadProgress, useFileUpload } from '@/utils/useFileUpload';
import { NIL } from 'uuid';
import { last } from 'lodash';
import BaseImage from '@/components/images/BaseImage.vue';
import { useNotificationStore } from '@/stores/notificationStore';

export interface FileUploadProgress {
  file?: File;
  uploadProgress?: UploadProgress;
  name: string;
  id: string;
}

defineOptions({
  inheritAttrs: false,
});

const { t } = useTranslation();

const {
  mode = 'edit',
  aspectRatio = undefined,
  disabled,
  modelValue,
  accept = '.gif, .jpg, .jpeg, .png',
} = defineProps<ImageFieldProps>();

const emits = defineEmits<{
  'update:modelValue': [string | undefined];
  'update:uploadProgress': [UploadProgress];
}>();

const uploadProgress = ref<UploadProgress>();
const notificationStore = useNotificationStore();

const defaultContextMenuActions = getDefaultContextMenuActions();
const contextMenuActions = computed(() => [
  {
    text: t('Upload'),
    icon: mdiUpload,
    disabled: disabled,
    callback: () => {
      open();
    },
  },
  {
    ...defaultContextMenuActions.delete,
    divider: false,
    disabled: !modelValue || disabled,
    callback: () => {
      notificationStore.hideBarMessage();
      emits('update:modelValue', undefined);
    },
  },
]);

const {
  open,
  reset,
  onChange: onFileDialogChange,
} = useFileDialog({
  accept,
  multiple: false,
});
const { uploadFile } = useFileUpload({
  ...defaultFileUploadOptions,
  blobContainerName: 'Pictures',
});

onFileDialogChange(async (files: FileList | null) => {
  if (files?.length) {
    const file = last(files)!;
    await upload({
      file,
      name: file.name,
      id: NIL,
      uploadProgress: {
        state: 'idle',
      },
    });
    reset();
  }
});

async function upload(fileInfoToUpload: FileUploadProgress) {
  let fileId: string | undefined = undefined;
  if (fileInfoToUpload.file) {
    return uploadFile({
      file: fileInfoToUpload.file,
      name: fileInfoToUpload.name,
      onUpdate: (newUploadProgress) => {
        uploadProgress.value = newUploadProgress;
        emits('update:uploadProgress', newUploadProgress);
      },
      onSuccess: () => {
        notificationStore.hideBarMessage();
        emits('update:modelValue', fileId);
      },
      onError: () => {
        notificationStore.error(t('UploadPictureError_accept_Msg', { accept }));
      },
      onReceivedId: (id: string) => {
        fileId = id;
      },
    });
  }
}

onUnmounted(() => {
  if (
    notificationStore.currentBarMessage?.type === 'error' &&
    notificationStore.currentBarMessage.message
  )
    notificationStore.hideBarMessage();
});
</script>

<template>
  <FormLabel
    :label="label"
    :disabled="disabled"
    :for="id"
    :class="[$attrs.class, 'wrapper']"
    :style="$attrs.style"
  >
    <VInput
      :error="Boolean(errorMessage)"
      :hint="errorMessage || hint"
      :persistentHint="Boolean(errorMessage) || persistentHint"
    >
      <div class="image-container">
        <BaseImage
          :class="['image', { faded: uploadProgress?.state === 'pending' }]"
          :pictureId="modelValue"
          :width="width"
          :height="height"
          :sizes="sizes"
          :srcset="srcset"
          :aspectRatio="aspectRatio"
          :mobileWidth="mobileWidth"
          :desktopWidth="desktopWidth"
          :fallbackIcon="fallbackIcon"
          :tooltip="tooltip"
          :rounded="rounded"
          :data-testid="$attrs['data-testid']"
          :cropMode="cropMode"
          :cover="cover"
        />
        <v-progress-linear
          v-if="uploadProgress?.state === 'pending'"
          class="upload-progress"
          :modelValue="uploadProgress.percentage"
          color="primary"
          height="4"
        />
        <div v-if="mode !== 'details'" class="contextMenu">
          <ContextMenu
            :actions="contextMenuActions"
            :data-testid="`${$attrs['data-testid']}_ContextMenu`"
          />
        </div>
      </div>
    </VInput>
  </FormLabel>
</template>

<style scoped lang="scss">
.wrapper {
  display: flex;
}

.image-container {
  position: relative;
  width: fit-content;
  max-width: 100%;
}

:deep(.image) {
  transition: opacity 0.4s ease;
}
:deep(.image.faded) {
  opacity: 0.4;
}

.upload-progress {
  position: absolute;
  border-radius: 4px;
  top: 50% !important;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 8px !important;

  /* Ensure contrast */
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.6);
  background-color: rgba(255, 255, 255, 0.4);
}

.contextMenu {
  position: absolute;
  margin: 2px;
  top: 0;
  right: 0;
}

.contextMenu:hover {
  background: white;
  opacity: 0.7;
  border-radius: 3px;
}
</style>
